import { deriveMasterKeyInWorker } from '@/crypto/argon2-worker-client'
import { aesGcmOpen } from '@/crypto/aes-gcm'
import { base64ToBytes, hexToBytes } from '@/crypto/encoding'
import { deriveStretchedMasterKeyEnc } from '@/crypto/hkdf'
import { withZeroize } from '@/crypto/zeroize'
import type { VaultUnlockMaterial } from './sessionStore'

/**
 * unlockFlow — LOCKED 中の `material` + 再入力 MP から SK を復元する。
 *
 * `KEY_HIERARCHY.md §4.5` と `SESSION_LIFECYCLE.md §6` のクライアント完結 unlock
 * フローを純粋関数として分離し、依存注入で testable に保つ。
 *
 * Step (SESSION_LIFECYCLE.md §6 step 3-7):
 *   3. MK = Argon2id(MP, kdfSalt, kdfParams)         (Worker)
 *   4. SMK_enc = HKDF(MK, "enc")                     (zeroize MK 直後)
 *   5. SK = AES-GCM-Open(SMK_enc, iv, protectedSymmetricKey)
 *      失敗時は authentication tag mismatch で `aesGcmOpen` が throw → caller が
 *      「マスターパスワードが正しくありません」として表示
 *   6. zeroize SMK_enc
 *   7. return SK (caller が sessionStore.unlock(SK) する)
 *
 * セキュリティ:
 *   - MP / MK / SMK_enc は `withZeroize` で関数スコープ内に閉じ込め、return 後も
 *     呼出側に漏らさない (CLAUDE.md §2 ルール 1-3 と整合)
 *   - サーバ往復なし — オフラインでも MP + material があれば SK を復元できる
 *   - wrong MP は AES-GCM の auth tag 検証で確実に弾かれる (KEY_HIERARCHY.md §4.5)
 */

export interface UnlockFlowDeps {
  deriveMasterKey: typeof deriveMasterKeyInWorker
  deriveStretchedMasterKeyEnc: typeof deriveStretchedMasterKeyEnc
  aesGcmOpen: typeof aesGcmOpen
}

const defaultDeps: UnlockFlowDeps = {
  deriveMasterKey: deriveMasterKeyInWorker,
  deriveStretchedMasterKeyEnc,
  aesGcmOpen,
}

/**
 * MP + material → SK を復元する。失敗 (wrong MP / 暗号文破損) は throw する。
 *
 * @param password 平文 MP — 関数を出ない
 * @param material `sessionStore.getMaterial()` から得る LOCKED 中の protected blob
 * @returns 32 byte SK。caller の責任で `sessionStore.unlock(sk)` 経由で保持する
 *   (zeroize は sessionStore 側で次の遷移時に行う)
 */
export const unlockWithMasterPassword = async (
  password: string,
  material: VaultUnlockMaterial,
  deps: UnlockFlowDeps = defaultDeps,
): Promise<Uint8Array> => {
  const kdfSalt = hexToBytes(material.kdfSalt)
  const iv = base64ToBytes(material.protectedSymmetricKeyIv)
  const sealedSk = base64ToBytes(material.protectedSymmetricKey)

  // step 3: MK = Argon2id(MP, kdfSalt, kdfParams) — Worker 経由でメインスレッド非ブロック
  const mk = await deps.deriveMasterKey(password, kdfSalt, {
    memoryCost: material.kdfParams.memoryCost,
    timeCost: material.kdfParams.timeCost,
    parallelism: material.kdfParams.parallelism,
  })

  return withZeroize([mk], async () => {
    // step 4: SMK_enc = HKDF(MK, "enc")
    const smkEnc = await deps.deriveStretchedMasterKeyEnc(mk)

    return withZeroize([smkEnc], async () => {
      // step 5: SK = AES-GCM-Open。wrong MP なら auth tag 不一致で throw
      return deps.aesGcmOpen(smkEnc, iv, sealedSk)
    })
  })
}
